import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
    Box,
    Typography,
    Grid,
    Button,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    Tooltip,
    Skeleton,
    Backdrop,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    TextField,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Drawer,
    Card,
    CardContent,
    Chip,
    Paper,
    Divider,
    Autocomplete
} from '@mui/material';

import { ThemeProvider, createTheme } from '@mui/material/styles';
import { MaterialReactTable } from 'material-react-table';
import { MRT_Localization_PT_BR } from 'material-react-table/locales/pt-BR';
import SensorsIcon from '@mui/icons-material/Sensors';
import ViewWeekIcon from '@mui/icons-material/ViewWeek';
import TableChartIcon from '@mui/icons-material/TableChart';
import PostAddIcon from '@mui/icons-material/PostAdd';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SettingsIcon from '@mui/icons-material/Settings';
import MapIcon from '@mui/icons-material/Map';
import RouteIcon from '@mui/icons-material/Route';
import DashboardIcon from '@mui/icons-material/Dashboard';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import DownloadIcon from '@mui/icons-material/Download';
import ArrowUpward from '@mui/icons-material/ArrowUpward';
import ArrowDownward from '@mui/icons-material/ArrowDownward';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import SecurityIcon from '@mui/icons-material/Security';
import FlagIcon from '@mui/icons-material/Flag';
import LooksOneIcon from '@mui/icons-material/LooksOne';
import LooksTwoIcon from '@mui/icons-material/LooksTwo';
import HomeIcon from '@mui/icons-material/Home';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { styled } from '@mui/material/styles';
import Cookies from 'js-cookie';
import api from '../../../../../services/api';
import NavegadorGestao from '../../../../../components/NavBarGestao';
import TimelineRonda from './components/TimelineRonda';
import LogInterativoRonda from './components/LogInterativoRonda';
import SinalizarStatus from './sinalizarStatus';
import MapaTempoReal from './MapaTempoReal';
import TabelaRotas from './components/TabelaRotas';


import moment from 'moment';
import ReactApexChart from 'react-apexcharts';

// 🧠 Função de segurança para parsear JSON apenas quando necessário
const safeParse = (value, fallback = {}) => {
    if (!value) return fallback;
    if (typeof value === 'object') return value; // já é um objeto
    try {
        return JSON.parse(value);
    } catch {
        return fallback;
    }
};

// Componente Auxiliar para o Detail Panel
const DetailBox = ({ label, value, darkMode, multiline }) => (
    <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" sx={{ color: darkMode ? '#aaa' : 'text.secondary', fontWeight: 'bold' }}>{label}:</Typography>
        <Typography variant="body1" sx={{ mt: 0.5, whiteSpace: multiline ? 'pre-line' : 'normal', color: darkMode ? '#eee' : '#333' }}>{value || '—'}</Typography>
    </Box>
);

const RondaDetailPanel = ({ rondaId, darkMode, themeHelper }) => {
    const [ronda, setRonda] = useState(null);
    const [loading, setLoading] = useState(true);
    const [elapsedTime, setElapsedTime] = useState("");

    const fixDateArray = (dateInput) => {
        if (Array.isArray(dateInput)) {
            const fixedDate = [...dateInput];
            if (fixedDate.length > 1) fixedDate[1] = fixedDate[1] - 1; 
            return moment(fixedDate);
        }
        return moment(dateInput);
    };

    useEffect(() => {
        const fetchDetails = async () => {
            try {
                const headers = { 'Authorization': 'Bearer ' + Cookies.get('token') };
                const res = await api.get("/v1/protected/rondamotorizada/listarporid/" + rondaId, { headers });
                setRonda(res.data);
                setLoading(false);
            } catch (err) {
                console.error("Erro ao buscar detalhes da ronda:", err);
                setLoading(false);
            }
        };
        fetchDetails();
    }, [rondaId]);

    useEffect(() => {
        if (!ronda || !ronda.data_hora_criacao) return;
        
        const updateElapsedTime = () => {
            const startTime = fixDateArray(ronda.data_hora_criacao);
            setElapsedTime(moment.duration(moment().diff(startTime)).humanize());
        };

        updateElapsedTime();
        const timer = setInterval(updateElapsedTime, 60000);
        return () => clearInterval(timer);
    }, [ronda]);

    if (loading) return <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}><CircularProgress /></Box>;
    if (!ronda) return <Box sx={{ p: 4 }}><Typography color="error">Erro ao carregar detalhes da ronda #{rondaId}.</Typography></Box>;

    const instalacao = safeParse(ronda.instalacao);
    const criador = safeParse(ronda.usuario_criador);
    const executor = safeParse(ronda.usuario_executor);
    const logs = Array.isArray(ronda.logs) ? ronda.logs : safeParse(ronda.logs, []);
    const procedimentos = Array.isArray(ronda.procedimentos) ? ronda.procedimentos : safeParse(ronda.procedimentos, []);

    // Criar um objeto ronda sanitizado para os subcomponentes
    const sanitizedRonda = { ...ronda, instalacao, usuario_criador: criador, usuario_executor: executor, logs, procedimentos };


    return (
        <Card variant="outlined" sx={{
            m: 2, p: 3, backgroundColor: darkMode ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)', color: themeHelper?.text || 'inherit', borderRadius: 3,
            boxShadow: 'none', borderColor: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
        }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5" color="primary" sx={{ fontWeight: 'bold' }}>Painel da Ronda #{sanitizedRonda.id_ronda_motorizada}</Typography>

                <Chip 
                    label={Number(ronda.status_ronda_motorizada) === 10 ? 'FINALIZADA' : 'EM ANDAMENTO'} 
                    color={Number(ronda.status_ronda_motorizada) === 10 ? 'success' : 'warning'}
                    sx={{ fontWeight: 'bold' }}
                />

            </Box>

            <Grid container spacing={4}>
                <Grid item xs={12} lg={7}>
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                            <DetailBox label="Data Criação" value={fixDateArray(ronda.data_hora_criacao).format("DD/MM/YYYY HH:mm")} darkMode={darkMode} />
                            <DetailBox label="Tempo Decorrido" value={elapsedTime} darkMode={darkMode} />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <DetailBox label="Instalação" value={instalacao.nome_fantasia || instalacao.razao_social || "N/A"} darkMode={darkMode} />
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                <Typography variant="subtitle2" sx={{ color: darkMode ? '#aaa' : 'text.secondary', fontWeight: 'bold' }}>Envolvidos:</Typography>
                                <Typography variant="caption" sx={{ color: darkMode ? '#eee' : '#333' }}>• <b>Criador:</b> {criador.nome_amigavel || "Sistema"}</Typography>
                                <Typography variant="caption" sx={{ color: darkMode ? '#eee' : '#333' }}>• <b>Executor:</b> {executor.nome_amigavel || "Pendente"}</Typography>
                            </Box>
                        </Grid>
                    </Grid>

                    <Divider sx={{ my: 3, borderColor: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)' }} />
                    
                    <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>Descrição / Observações</Typography>
                    <Box sx={{ bgcolor: darkMode ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)', p: 2, borderRadius: 2, mb: 3 }}>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-line', color: darkMode ? '#eee' : '#333' }}>{ronda.descricao || 'Sem descrição.'}</Typography>
                    </Box>

                    {Number(ronda.status_ronda_motorizada) === 10 && (
                        <>
                            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1, color: 'success.main' }}>Observações do Encerramento</Typography>
                            <Box sx={{ bgcolor: 'rgba(76, 175, 80, 0.1)', p: 2, borderRadius: 2, mb: 3, border: '1px solid rgba(76, 175, 80, 0.2)' }}>
                                <Typography variant="body2" sx={{ whiteSpace: 'pre-line', color: darkMode ? '#dfd' : '#2e7d32' }}>{ronda.observacoes_encerramento_ronda || "Ronda encerrada sem observações finais."}</Typography>
                            </Box>
                        </>
                    )}

                    <Divider sx={{ my: 3, borderColor: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)' }} />
                    
                    {/* EXIBIÇÃO DE QUESTIONÁRIO E PROBLEMAS */}
                    {procedimentos?.map((proc) => (
                        <Box key={proc.id_procedimento_execucao} sx={{ mt: 4 }}>
                            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, color: darkMode ? '#90caf9' : '#1976d2' }}>
                                {proc.procedimento.nome}
                            </Typography>
                            
                            {/* Questões */}
                            {proc.questao_resposta && proc.questao_resposta.length > 0 && (
                                <Box sx={{ mb: 3 }}>
                                    <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', color: darkMode ? '#aaa' : '#666', textTransform: 'uppercase', mb: 1 }}>Questionário</Typography>
                                    <Grid container spacing={2}>
                                        {proc.questao_resposta.map((q) => (
                                            <Grid item xs={12} md={6} key={q.id_questao_resposta}>
                                                <Box sx={{ p: 2, borderRadius: 2, border: darkMode ? '1px solid #333' : '1px solid #eee', bgcolor: darkMode ? 'rgba(255,255,255,0.02)' : '#fafafa' }}>
                                                    <Typography variant="caption" color="textSecondary" sx={{ display: 'block', fontWeight: 'bold', mb: 0.5 }}>{q.nome}</Typography>
                                                    <Typography variant="body2" sx={{ fontWeight: 'bold', color: darkMode ? '#fff' : '#444' }}>
                                                        {q.tipo === 1 ? (q.escolha_fixa === 1 ? "SIM" : "NÃO") : (q.resposta_livre || "Sem resposta")}
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                        ))}
                                    </Grid>
                                </Box>
                            )}

                            {/* Problemas */}
                            {proc.problemas && proc.problemas.length > 0 && (
                                <Box sx={{ mb: 2 }}>
                                    <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', color: '#f44336', textTransform: 'uppercase', mb: 1 }}>Problemas Identificados</Typography>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                        {proc.problemas.map((p) => (
                                            <Chip key={p.id_problema} label={p.nome} color="error" variant="outlined" size="small" sx={{ fontWeight: 'bold' }} />
                                        ))}
                                    </Box>
                                </Box>
                            )}
                            <Divider sx={{ my: 3, opacity: 0.3 }} />
                        </Box>
                    ))}

                    <TimelineRonda ronda={sanitizedRonda} darkMode={darkMode} />

                </Grid>

                <Grid item xs={12} lg={5}>
                    <LogInterativoRonda rondaId={ronda.id_ronda_motorizada} darkMode={darkMode} themeHelper={themeHelper} />
                </Grid>
            </Grid>
        </Card>
    );
};


export default function RondaMotorizada() {
    const [value, setValue] = useState(0);
    const [menuOpen, setMenuOpen] = useState(false);
    const [viewMode, setViewMode] = useState('table');
    const [rondasMotorizadas, setRondasMotorizadas] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [darkMode, setDarkMode] = useState(Cookies.get('darkMode') === 'true');

    const globalTheme = useMemo(() => createTheme({
        palette: {
            mode: darkMode ? 'dark' : 'light',
            primary: { main: '#2196f3' },
            background: {
                default: darkMode ? '#121212' : '#f5f5f5',
                paper: darkMode ? '#1e1e1e' : '#fff',
            },
        },
    }), [darkMode]);


    // Dashboard states
    const [loadingDashboard, setLoadingDashboard] = useState(true);
    const [infoDashboard, setInfoDashboard] = useState({
        dashboardInfo: {
            totalHoje: 0,
            totalOntem: 0,
            duracaoHoje: 0,
            duracaoOntem: 0,
            totalMes: 0,
            totalMesPassado: 0
        },
        rondasMensaisParaGrafico: [],
        rankingDiarioHoje: [],
        rankingDiarioOntem: [],
        rankingDiarioMes: [],
        rankingDiarioTodoPeriodo: [],
        dadosMeta: {
            objetivo: 0,
            meta: 0,
            executadoumaronda: 0,
            executadoduasrondas: 0
        }
    });
    const [rankingSelecao, setRankingSelecao] = useState('Hoje');

    // Relatoria states
    const [gruposControle, setGruposControle] = useState([]);
    const [grupoControleSelecionado, setGrupoControleSelecionado] = useState(null);
    const [month, setMonth] = useState(String(new Date().getMonth() + 1).padStart(2, '0'));
    const [year, setYear] = useState(new Date().getFullYear());
    const [loadingRelatoria, setLoadingRelatoria] = useState(false);
    const [inGeracao, setInGeracao] = useState(false);



    const [openHistorico, setOpenHistorico] = useState(false);
    const [historicoRankingMensal, setHistoricoRankingMensal] = useState([]);
    const [mesObservado, setMesObservado] = useState("");
    const [rankingDoGrafico, setRankingDoGrafico] = useState([]);
    const historicoRef = useRef([]);

    const [statusOnlineLocal, setStatusOnlineLocal] = useState(false);
    const [openSinalizarStatus, setOpenSinalizarStatus] = useState(false);

    const regras = Cookies.get("regra") || "";

    const months = [
        { value: '01', label: 'Janeiro' }, { value: '02', label: 'Fevereiro' },
        { value: '03', label: 'Março' }, { value: '04', label: 'Abril' },
        { value: '05', label: 'Maio' }, { value: '06', label: 'Junho' },
        { value: '07', label: 'Julho' }, { value: '08', label: 'Agosto' },
        { value: '09', label: 'Setembro' }, { value: '10', label: 'Outubro' },
        { value: '11', label: 'Novembro' }, { value: '12', label: 'Dezembro' }
    ];

    useEffect(() => {
        listarInfoDashboard();
        fetchUserStatus();
        
        const handleDarkModeChange = () => setDarkMode(Cookies.get('darkMode') === 'true');
        window.addEventListener('darkModeChanged', handleDarkModeChange);
        
        // --- POLLING DE PÂNICO ---
        const checkPanic = async () => {
            try {
                const res = await api.get("/v1/protected/rondamotorizada/listarLogsRecentes?apenasPendentes=true", { 
                    headers: { 'Authorization': 'Bearer ' + Cookies.get('token') } 
                });
                const recents = (res.data || []).filter(l => 
                    l.tipo === "PANICO" && 
                    moment().diff(moment(l.data_hora), 'minutes') < 15
                );
                if (recents.length > 0) {
                    setPanicAlerts(recents);
                    new Audio('https://www.soundjay.com/buttons/beep-01a.mp3').play().catch(() => {});
                } else {
                    setPanicAlerts([]);
                }
            } catch (e) {}
        };
        const panicTimer = setInterval(checkPanic, 20000);
        checkPanic();

        return () => {
            window.removeEventListener('darkModeChanged', handleDarkModeChange);
            clearInterval(panicTimer);
        }
    }, []);

    const [panicAlerts, setPanicAlerts] = useState([]);
    const [acknowledgedAlerts, setAcknowledgedAlerts] = useState([]);
    const [panicLogsHistory, setPanicLogsHistory] = useState([]);
    const [openPanicLogs, setOpenPanicLogs] = useState(false);

    async function fetchUserStatus() {
        try {
            const idUsuario = Cookies.get('id_usuario');
            if (!idUsuario) return;
            const headers = { 'Authorization': 'Bearer ' + Cookies.get('token') };
            const res = await api.get(`/v1/protected/user/retornardadosusuario/${idUsuario}`, { headers });
            if (res.data) setStatusOnlineLocal(res.data.status_ronda_motorizada_online === 1);
        } catch (err) { console.error(err); }
    }

    const listarInfoDashboard = async () => {
        setLoadingDashboard(true);
        try {
            const headers = { 'Authorization': 'Bearer ' + Cookies.get("token") };
            const response = await api.get('v1/protected/rondamotorizada/listarinfodashboard', { headers });
            if (response.data) setInfoDashboard(response.data);
        } catch (error) { console.error(error); } finally { setLoadingDashboard(false); }
    };

    const listarObjetos = async () => {
        setLoading(true);
        try {
            const headers = { 'Authorization': 'Bearer ' + Cookies.get("token") };
            const response = await api.get("v1/protected/rondamotorizada/listarresumidas", { headers });
            if (response.data) {
                const updated = response.data.map(item => ({
                    ...item,
                    conta_instalacao: `${item.identificador_integrador} - ${item.tipo_instalacao === 0 ? (item.nome + ' ' + (item.sobrenome || '')) : (item.nome_empresarial || item.nome_fantasia)}`,
                    status: item.status_ronda_motorizada === 10 ? "FINALIZADA" : (item.status_ronda_motorizada === 0 ? (item.data_hora_designacao ? "EM EXECUÇÃO" : "DISPONÍVEL") : (item.status_ronda_motorizada === 1 ? "ENCERRADO" : "INDEFINIDO"))
                }));
                const sorted = updated.sort((a, b) => b.id_ronda_motorizada - a.id_ronda_motorizada);
                setRondasMotorizadas(sorted);
                setFilteredData(sorted);
            }
        } catch (error) { console.error(error); } finally { setLoading(false); }
    };

    const listarGruposControle = async () => {
        setLoadingRelatoria(true);
        try {
            const headers = { 'Authorization': 'Bearer ' + Cookies.get("token") };
            const response = await api.get("v1/protected/grupocontrole/listartodos", { headers });
            if (response.data) setGruposControle(response.data);
        } catch (error) { console.error(error); } finally { setLoadingRelatoria(false); }
    };

    const gerarRelatorioRondaMotorizada = async () => {
        if (!grupoControleSelecionado) return alert("Selecione o Grupo de Controle");
        setInGeracao(true);
        try {
            const dados = { tipo_grupo: 1, id_usuario: Cookies.get("id_usuario"), id_grupo: grupoControleSelecionado.id_grupo_controle, mes: month, ano: year };
            const headers = { 'Authorization': 'Bearer ' + Cookies.get("token") };
            const response = await api.put('/v1/protected/rondamotorizada/gerarrelatorio', dados, { headers });
            if (response.data?.caminho_arquivo) {
                window.open(response.data.caminho_arquivo.replace("/home/wwgrup/rondamotorizada/", "https://gruporosinetos.com/rvmidias/"), "_blank");
            } else alert("Nenhum dado encontrado.");
        } catch (error) { alert("Erro ao gerar relatório"); } finally { setInGeracao(false); }
    };



    const listarLogsPanico = async () => {
        setLoading(true);
        try {
            const headers = { 'Authorization': 'Bearer ' + Cookies.get('token') };
            const res = await api.get("/v1/protected/rondamotorizada/listarLogsRecentes", { headers });
            // Filtra todos os pânicos do histórico
            setPanicLogsHistory((res.data || []).filter(l => l.tipo === "PANICO").sort((a,b) => b.id_log_ronda_motorizada - a.id_log_ronda_motorizada));
        } catch (err) { console.error(err); } finally { setLoading(false); }
    };

    const formatAudioUrl = (path) => {
        if (!path) return null;
        if (path.startsWith('http')) return path; 
        // Padronização: Substitui o caminho físico pelo proxy
        return path.replace('/home/wwgrup/rondamotorizada/', 'https://gruporosinetos.com/rmmidias/');
    };

    const listarHistorico = async () => {
        try {
            const headers = { 'Authorization': 'Bearer ' + Cookies.get('token') };
            const response = await api.get("/v1/protected/rondamotorizada/historico", { headers });
            const data = response.data;
            setHistoricoRankingMensal(data);
            historicoRef.current = data;
            
            const categorias = data.map(item => `${String(item.mes).padStart(2, '0')}/${item.ano}`);
            const valores = data.map(item => item.usuarios.reduce((s, u) => s + u.totalRondas, 0));
            
            setDadosHistorico(prev => ({
                ...prev,
                series: [{ name: 'Rondas', data: valores }],
                options: { ...prev.options, xaxis: { ...prev.options.xaxis, categories: categorias } }
            }));
            setOpenHistorico(true);
        } catch (err) { console.error(err); }
    };

    const handleClickNoPonto = (label) => {
        const [m, a] = label.split('/').map(Number);
        const item = historicoRef.current.find(h => h.mes === m && h.ano === a);
        if (item) setRankingDoGrafico([...item.usuarios].sort((x, y) => y.totalRondas - x.totalRondas));
        setMesObservado(label);
    };

    const [dadosHistorico, setDadosHistorico] = useState({
        series: [{ name: 'Rondas', data: [] }],
        options: {
            chart: { height: 500, type: 'line', toolbar: { show: true }, events: { dataPointSelection: (e, c, o) => handleClickNoPonto(o.w.globals.categoryLabels[o.dataPointIndex]) } },
            stroke: { width: 4, curve: 'smooth' },
            xaxis: { categories: [], title: { text: 'Mês/Ano' } },
            markers: { size: 5 },
            tooltip: { shared: false, intersect: true }
        }
    });

    const dadosGraficosMain = useMemo(() => {
        if (!infoDashboard?.rondasMensaisParaGrafico) return { series: [], options: {} };
        const cats = infoDashboard.rondasMensaisParaGrafico.map(i => {
            const d = new Date(i.data);
            return `${d.getDate()} ${d.toLocaleString('default', { month: 'short' })}`;
        });
        return {
            series: [{ name: 'Rondas', data: infoDashboard.rondasMensaisParaGrafico.map(i => i.rondas) }],
            options: {
                chart: { type: 'line', toolbar: { show: false }, foreColor: darkMode ? '#fff' : '#000' },
                stroke: { width: 4, curve: 'smooth' },
                xaxis: { categories: cats },
                dataLabels: { enabled: true }
            }
        };
    }, [infoDashboard, darkMode]);

    const columns = useMemo(() => [
        {
            accessorKey: 'id_ronda_motorizada',
            header: 'Ações',
            size: 120,
            Cell: ({ row }) => (
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Tooltip title="Editar Ronda Motorizada">
                        <IconButton
                            onClick={(e) => {
                                e.stopPropagation();
                                window.open('/gestao/operacional/rondamotorizada/gerenciar/' + row.original.id_ronda_motorizada, '_blank');
                            }}
                            sx={{ color: darkMode ? '#90caf9' : '#1976d2' }}
                        >
                            <EditIcon />
                        </IconButton>
                    </Tooltip>
                    {regras.includes("ROOT_MASTER") && (
                        <Tooltip title="Deletar">
                            <IconButton 
                                sx={{ color: 'red' }} 
                                onClick={(e) => { 
                                    e.stopPropagation();
                                    if(window.confirm('Deletar?')) 
                                        api.delete('v1/protected/rondamotorizada/deletar/'+row.original.id_ronda_motorizada, {headers:{'Authorization':'Bearer '+Cookies.get('token')}})
                                            .then(()=>listarObjetos()) 
                                }}
                            >
                                <DeleteIcon />
                            </IconButton>
                        </Tooltip>
                    )}
                </Box>
            )
        },
        { accessorKey: 'id_ronda_motorizada', header: 'ID', size: 80 },
        { accessorKey: 'status', header: 'STATUS', Cell: ({ cell, row }) => {
            const val = cell.getValue();
            const status = row.original.status_ronda_motorizada;
            let color = 'default';
            if (status === 10) color = 'success';
            else if (status === 0) {
                color = row.original.data_hora_designacao ? 'warning' : 'info';
            } else if (status === 1) color = 'error';
            
            return <Chip label={val} color={color} size="small" sx={{ fontWeight: 'bold' }} />;
        }},

        { accessorKey: 'conta_instalacao', header: 'Instalação / Conta', size: 300 },
        { accessorKey: 'nome_amigavel', header: 'Executor', size: 150 },
        { accessorKey: 'data_hora_finalizacao', header: 'Finalização', Cell: ({ cell }) => cell.getValue() ? moment(cell.getValue()).format('HH:mm DD/MM/YYYY') : '-' },
        { accessorKey: 'duracao', header: 'Duração', size: 100 }
    ], [regras, darkMode]);

    const tableTheme = createTheme({ palette: { mode: darkMode ? 'dark' : 'light' } });

    const handleMenuClick = (v) => {
        if (v === 0) listarInfoDashboard();
        else if (v === 1) listarObjetos();
        else if (v === 2) listarGruposControle();
        else if (v === 5) listarLogsPanico();
        // v === 4 for Rotas will have its own fetch inside the component or here if needed
        setValue(v);
    };


    const renderKanbanView = () => (
        <Grid container spacing={2} sx={{ p: 2 }}>
            {[0, 1].map(s => (
                <Grid item xs={12} md={6} key={s}>
                    <Paper sx={{ p: 2, bgcolor: darkMode ? '#1e1e1e' : '#f0f2f5', minHeight: '60vh' }}>
                        <Typography variant="h6" sx={{ mb: 2, borderBottom: '2px solid', borderColor: s === 0 ? 'orange' : 'green' }}>
                            {s === 0 ? 'Em Execução / Disponíveis' : 'Concluídas'}
                        </Typography>
                        {filteredData.filter(r => r.status_ronda_motorizada === s).map((r, i) => (
                            <Card key={i} sx={{ mb: 2, cursor: 'pointer', borderLeft: `5px solid ${s === 0 ? 'orange' : 'green'}` }} onClick={() => window.open('/gestao/operacional/rondamotorizada/gerenciar/' + r.id_ronda_motorizada, '_blank')}>

                                <CardContent sx={{ p: 2 }}>
                                    <Typography variant="subtitle2" fontWeight="bold">{r.conta_instalacao}</Typography>
                                    <Typography variant="caption" color="textSecondary">{r.nome_amigavel} | {r.duracao} min</Typography>
                                </CardContent>
                            </Card>
                        ))}
                    </Paper>
                </Grid>
            ))}
        </Grid>
    );

    return (
        <ThemeProvider theme={globalTheme}>
            <div style={{ backgroundColor: globalTheme.palette.background.default, color: darkMode ? '#fff' : 'inherit', minHeight: '100vh' }}>
                <NavegadorGestao Operacional={true} RondaMotorizada={true} corFundo={darkMode ? '#1e1e1e' : 'white'} travado={true} corTexto={darkMode ? 'white' : 'black'} />

                {/* BANNER DE ALERTA DE PÂNICO */}
                {panicAlerts.length > 0 && (
                    <Box sx={{ 
                        bgcolor: '#d32f2f', 
                        color: '#fff', 
                        p: 2, 
                        textAlign: 'center', 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        gap: 2,
                        zIndex: 2000,
                        animation: 'pulsePanic 1s infinite alternate',
                        '@keyframes pulsePanic': {
                            'from': { backgroundColor: '#d32f2f' },
                            'to': { backgroundColor: '#b71c1c' },
                        }
                    }}>
                        <SecurityIcon sx={{ fontSize: 32 }} />
                        <Box sx={{ flex: 1, textAlign: 'left' }}>
                            <Typography variant="h6" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>ALERTA DE PÂNICO ACIONADO!</Typography>
                            <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                                Rondante(s) em perigo: {panicAlerts.map(p => p.usuario?.nome_amigavel).filter((v, i, a) => a.indexOf(v) === i).join(', ')}
                            </Typography>
                            <Typography variant="caption" sx={{ opacity: 0.9 }}>
                                Detectamos {panicAlerts.length} alerta(s). Localização e áudio disponíveis nos logs.
                            </Typography>
                        </Box>
                        <Button 
                            variant="contained" 
                            size="small"
                            color="inherit" 
                            sx={{ color: '#d32f2f', fontWeight: 'bold', ml: 2 }}
                            onClick={() => setOpenPanicLogs(true)}
                        >
                            VER LOGS
                        </Button>
                        <Button 
                            variant="outlined" 
                            size="small"
                            color="inherit" 
                            sx={{ color: '#fff', fontWeight: 'bold', ml: 1 }}
                            onClick={async () => {
                                const idGestor = Cookies.get('id_usuario');
                                for (const panic of panicAlerts) {
                                    await api.post("/v1/protected/rondamotorizada/finalizarAlertaPanico", {
                                        id_log: panic.id_log_ronda_motorizada,
                                        id_usuario_gestor: idGestor
                                    }, { headers: { 'Authorization': 'Bearer ' + Cookies.get('token') } });
                                }
                                setPanicAlerts([]);
                                listarLogsPanico(); // Atualiza a aba de alertas se estiver nela
                            }}
                        >
                            CIENTE
                        </Button>
                    </Box>
                )}


            {value === 0 && !loadingDashboard && (
                <div style={{ paddingLeft: menuOpen ? 220 : 65, transition: 'padding-left 0.3s', paddingTop: 10, width: '100%' }}>
                    <Grid container spacing={2} sx={{ p: 2 }}>
                        {[
                            { label: 'Metas', val: infoDashboard.dadosMeta.objetivo, sub: `${infoDashboard.dadosMeta.meta} Meta`, icon: <FlagIcon />, color: '#00008B' },
                            { label: 'Rondas Hoje', val: infoDashboard.dashboardInfo.totalHoje, sub: `${infoDashboard.dashboardInfo.totalOntem} Ontem`, icon: <SecurityIcon />, color: '#00008B' },
                            { label: 'Média Hoje', val: `${parseInt(infoDashboard.dashboardInfo.duracaoHoje)} min`, sub: `${parseInt(infoDashboard.dashboardInfo.duracaoOntem)} Ontem`, icon: <AccessTimeIcon />, color: '#191970' },
                            { label: 'Total Mês', val: infoDashboard.dashboardInfo.totalMes, sub: `${infoDashboard.dashboardInfo.totalMesPassado} Passado`, icon: <SecurityIcon />, color: '#008B8B' }
                        ].map((c, i) => (
                            <Grid item xs={12} sm={3} key={i}>
                                <Box sx={{ p: 2, bgcolor: c.color, color: 'white', borderRadius: 2, textAlign: 'center', boxShadow: 3 }}>
                                    <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>{c.icon} {c.label}</Typography>
                                    <Typography variant="h4" fontWeight="bold">{c.val}</Typography>
                                    <Typography variant="caption">{c.sub}</Typography>
                                </Box>
                            </Grid>
                        ))}
                        <Grid item xs={12} md={8}>
                            <Paper sx={{ p: 2, bgcolor: darkMode ? '#1e1e1e' : '#fff' }}>
                                <ReactApexChart options={dadosGraficosMain.options} series={dadosGraficosMain.series} type="line" height={350} />
                            </Paper>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Paper sx={{ p: 2, bgcolor: '#191970', color: 'white', maxHeight: 400, overflow: 'auto' }}>
                                <Typography variant="subtitle1" fontWeight="bold">Ranking {rankingSelecao}</Typography>
                                {infoDashboard[`rankingDiario${rankingSelecao}`]?.map((r, i) => (
                                    <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', py: 1, borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                                        <Typography variant="body2">{i === 0 ? '👑' : i + 1}. {r.nomeAmigavel}</Typography>
                                        <Typography variant="body2">{r.rondas}</Typography>
                                    </Box>
                                ))}
                                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 2 }}>
                                    {['Hoje', 'Ontem', 'Mes'].map(s => <Chip key={s} label={s} size="small" onClick={() => setRankingSelecao(s)} sx={{ bgcolor: rankingSelecao === s ? 'orange' : 'rgba(255,255,255,0.1)', color: 'white' }} />)}
                                </Box>
                            </Paper>
                            <Button fullWidth variant="contained" sx={{ mt: 2 }} onClick={listarHistorico}>Histórico Completo</Button>
                        </Grid>
                    </Grid>
                </div>
            )}

            {value === 1 && (
                <div style={{ paddingLeft: menuOpen ? 220 : 65, transition: 'padding-left 0.3s', paddingTop: 10 }}>
                    <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <IconButton onClick={() => setViewMode('table')} color={viewMode === 'table' ? 'primary' : 'default'}><TableChartIcon /></IconButton>
                            <IconButton onClick={() => setViewMode('kanban')} color={viewMode === 'kanban' ? 'primary' : 'default'}><ViewWeekIcon /></IconButton>
                            <Typography variant="h6">{filteredData.length} Rondas</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <Button variant="contained" onClick={() => window.open('/gestao/operacional/rondamotorizada/cadastrar', '_blank')}>Nova Ronda</Button>
                            <Button variant="contained" color={statusOnlineLocal ? "success" : "secondary"} startIcon={<SensorsIcon />} onClick={() => setOpenSinalizarStatus(true)}>{statusOnlineLocal ? "Online" : "Offline"}</Button>
                        </Box>
                    </Box>
                    <Box sx={{ px: 2 }}>
                        {loading ? (
                            <Skeleton variant="rectangular" height={400} />
                        ) : (
                            viewMode === 'table' ? (
                                <ThemeProvider theme={tableTheme}>
                                    <MaterialReactTable 
                                        columns={columns} 
                                        data={filteredData} 
                                        localization={MRT_Localization_PT_BR} 
                                        enableRowActions={false} 
                                        enableExpanding={true}
                                        renderDetailPanel={({ row }) => (
                                            <RondaDetailPanel rondaId={row.original.id_ronda_motorizada} darkMode={darkMode} themeHelper={{ text: darkMode ? '#fff' : '#000' }} />
                                        )}

                                        muiTableBodyRowProps={{ sx: { cursor: 'pointer' } }} 
                                    />
                                </ThemeProvider>
                            ) : renderKanbanView()
                        )}
                    </Box>
                </div>
            )}

            {value === 2 && (
                <div style={{ paddingLeft: menuOpen ? 220 : 65, transition: 'padding-left 0.3s', paddingTop: 10, p: 4 }}>
                    <Typography variant="h5" sx={{ mb: 4 }}>Relatórios Ronda Motorizada</Typography>
                    <Grid container spacing={3} alignItems="center">
                        <Grid item xs={12} md={5}>
                            <Autocomplete options={gruposControle} getOptionLabel={(g) => g.id_grupo_controle + ' - ' + g.nome} value={grupoControleSelecionado} onChange={(e, v) => setGrupoControleSelecionado(v)} renderInput={(params) => <TextField {...params} label="Grupo de Controle" />} />
                        </Grid>
                        <Grid item xs={6} md={2}>
                            <FormControl fullWidth><InputLabel>Mês</InputLabel><Select value={month} onChange={(e) => setMonth(e.target.value)} label="Mês">{months.map(m => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}</Select></FormControl>
                        </Grid>
                        <Grid item xs={6} md={2}>
                            <TextField fullWidth label="Ano" value={year} onChange={(e) => setYear(e.target.value.replace(/\D/g,''))} />
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <Button fullWidth variant="contained" size="large" onClick={gerarRelatorioRondaMotorizada} disabled={inGeracao}>Gerar PDF</Button>
                        </Grid>
                    </Grid>
                </div>
            )}

            {value === 3 && (
                <div style={{ paddingLeft: menuOpen ? 220 : 65, transition: 'padding-left 0.3s', paddingTop: 10, height: 'calc(100vh - 100px)' }}>
                    <MapaTempoReal 
                        darkMode={darkMode} 
                        onOpenRonda={id => window.open('/gestao/operacional/rondamotorizada/gerenciar/' + id, '_blank')} 
                        panicAlerts={panicAlerts}
                    />
                </div>

            )}
            
            {value === 4 && (
                <div style={{ paddingLeft: menuOpen ? 220 : 65, transition: 'padding-left 0.3s', paddingTop: 10 }}>
                     <TabelaRotas darkMode={darkMode} />
                </div>
            )}

            {value === 5 && (
                <div style={{ paddingLeft: menuOpen ? 220 : 65, transition: 'padding-left 0.3s', paddingTop: 10 }}>
                     <Box sx={{ p: 3 }}>
                        <Typography variant="h5" fontWeight="bold" gutterBottom>Histórico de Alertas de Pânico</Typography>
                        <MaterialReactTable 
                            columns={[
                                { accessorKey: 'data_hora', header: 'Data/Hora', Cell: ({ cell }) => moment(cell.getValue()).format("DD/MM/YYYY HH:mm:ss") },
                                { accessorKey: 'usuario.nome_amigavel', header: 'Rondante' },
                                { accessorKey: 'usuario_visto.nome_amigavel', header: 'Operador', Cell: ({ cell }) => cell.getValue() || '—' },
                                { accessorKey: 'data_hora_visto', header: 'Visto em', Cell: ({ cell }) => cell.getValue() ? moment(cell.getValue()).format("DD/MM HH:mm") : '—' },
                                { accessorKey: 'descricao', header: 'Descrição' },
                                { 
                                    accessorKey: 'audio_url', 
                                    header: 'Áudio',
                                    Cell: ({ cell }) => {
                                        const url = formatAudioUrl(cell.getValue());
                                        return url ? (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <audio key={url} src={url} controls style={{ height: '30px', width: '180px' }} />
                                                <IconButton size="small" component="a" href={url} download target="_blank">
                                                    <DownloadIcon fontSize="small" />
                                                </IconButton>
                                            </Box>
                                        ) : '—';
                                    }
                                },
                                {
                                    accessorKey: 'id_log_ronda_motorizada',
                                    header: 'Mapa',
                                    Cell: ({ row }) => (
                                        <Button size="small" variant="outlined" onClick={() => {
                                            setValue(3);
                                        }}>Ver Local</Button>
                                    )
                                }
                            ]}
                            data={panicLogsHistory}
                            localization={MRT_Localization_PT_BR}
                            state={{ isLoading: loading }}
                        />
                     </Box>
                </div>
            )}

            {/* MODAL DE LOGS DE PÂNICO */}
            <Dialog 
                open={openPanicLogs} 
                onClose={() => setOpenPanicLogs(false)}
                maxWidth="md"
                fullWidth
                PaperProps={{ sx: { borderRadius: 3, bgcolor: darkMode ? '#1e1e1e' : '#fff', color: darkMode ? '#fff' : 'inherit' } }}
            >
                <DialogTitle sx={{ borderBottom: '1px solid rgba(128,128,128,0.2)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6" fontWeight="bold">Histórico de Alertas de Pânico (Recentes)</Typography>
                    <IconButton onClick={() => setOpenPanicLogs(false)} color="inherit"><CloseIcon /></IconButton>
                </DialogTitle>
                <DialogContent sx={{ p: 0 }}>
                    <List sx={{ p: 0 }}>
                        {panicAlerts.length === 0 ? (
                            <Box sx={{ p: 4, textAlign: 'center' }}><Typography>Nenhum alerta pendente.</Typography></Box>
                        ) : (
                            panicAlerts.map((panic, idx) => (
                                <ListItem key={idx} sx={{ borderBottom: '1px solid rgba(128,128,128,0.1)', py: 2, px: 3, display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', mb: 1 }}>
                                        <Typography variant="subtitle1" fontWeight="bold" color="error">
                                            ⚠️ {panic.usuario?.nome_amigavel || 'Rondante Desconhecido'}
                                        </Typography>
                                        <Typography variant="caption" sx={{ opacity: 0.7 }}>
                                            {moment(panic.data_hora).format("DD/MM HH:mm:ss")} ({moment(panic.data_hora).fromNow()})
                                        </Typography>
                                    </Box>
                                    
                                    <Typography variant="body2" sx={{ mb: 2 }}>{panic.descricao}</Typography>
                                    
                                    <Box sx={{ display: 'flex', gap: 2, width: '100%', alignItems: 'center', flexWrap: 'wrap' }}>
                                        {panic.audio_url ? (() => {
                                            const url = formatAudioUrl(panic.audio_url);
                                            return (
                                                <Box sx={{ flex: 1, minWidth: '250px' }}>
                                                    <Typography variant="caption" display="block" gutterBottom>Evidência em Áudio:</Typography>
                                                    <Box sx={{ flex: 1, minWidth: '250px', display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <audio key={url} src={url} controls style={{ width: '100%', height: '35px' }} />
                                                        <IconButton component="a" href={url} download target="_blank">
                                                            <DownloadIcon />
                                                        </IconButton>
                                                    </Box>
                                                </Box>
                                            );
                                        })() : (
                                            <Typography variant="caption" color="textSecondary">Áudio não disponível ou em upload...</Typography>
                                        )}
                                        
                                        <Button 
                                            variant="outlined" 
                                            size="small" 
                                            startIcon={<MapIcon />}
                                            onClick={() => {
                                                setValue(3); // Vai para o mapa
                                                setOpenPanicLogs(false);
                                            }}
                                        >
                                            Ver no Mapa
                                        </Button>
                                    </Box>
                                </ListItem>
                            ))
                        )}
                    </List>
                </DialogContent>
            </Dialog>

            <Drawer variant="permanent" sx={{ '& .MuiDrawer-paper': { width: menuOpen ? 220 : 65, transition: 'width 0.3s', top: 100, height: 'calc(100vh - 100px)', bgcolor: darkMode ? '#1e1e1e' : '#fff', overflowX: 'hidden' } }}>

                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: menuOpen ? 'flex-end' : 'center', p: 1, borderBottom: '1px solid rgba(128,128,128,0.2)' }}>
                    <IconButton onClick={() => setMenuOpen(!menuOpen)} sx={{ color: darkMode ? '#fff' : '#000' }}>
                        {menuOpen ? <ChevronLeftIcon /> : <MenuIcon />}
                    </IconButton>
                </Box>
                <List>
                    {[
                        { l: 'Dashboard', i: <DashboardIcon />, v: 0 },
                        { l: 'Rondas', i: <PostAddIcon />, v: 1 },
                        { l: 'Rotas', i: <RouteIcon />, v: 4 },
                        { l: 'Alertas', i: <SecurityIcon />, v: 5 },
                        { l: 'Relatoria', i: <TableChartIcon />, v: 2 },
                        { l: 'Mapa', i: <MapIcon />, v: 3 }

                    ].map(item => (
                        <ListItem button key={item.v} onClick={() => handleMenuClick(item.v)} sx={{ bgcolor: value === item.v ? 'orange' : 'transparent', '&:hover': { bgcolor: 'rgba(255,165,0,0.2)' } }}>
                            <ListItemIcon sx={{ color: value === item.v ? 'white' : 'inherit' }}>{item.i}</ListItemIcon>
                            <ListItemText primary={item.l} sx={{ color: value === item.v ? 'white' : 'inherit' }} />
                        </ListItem>
                    ))}
                </List>
            </Drawer>



            <Dialog open={openHistorico} onClose={() => setOpenHistorico(false)} maxWidth="xl" fullWidth PaperProps={{ sx: { bgcolor: darkMode ? '#1e1e1e' : '#fff', color: darkMode ? '#fff' : 'inherit', borderRadius: 3 } }}>
                <DialogContent dividers sx={{ borderColor: darkMode ? '#333' : '#eee', display: 'flex', gap: 2, height: 600 }}>
                    <Box sx={{ flex: 3 }}><ReactApexChart options={dadosHistorico.options} series={dadosHistorico.series} type="line" height="100%" /></Box>
                    <Box sx={{ flex: 1, bgcolor: '#191970', color: 'white', p: 2, borderRadius: 2, overflow: 'auto' }}>
                        <Typography variant="h6" align="center">{mesObservado}</Typography>
                        {rankingDoGrafico.map((r, i) => (
                            <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', py: 1, borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                                <Typography variant="body2">{i+1}. {r.nomeAmigavel}</Typography>
                                <Typography variant="body2">{r.totalRondas}</Typography>
                            </Box>
                        ))}
                    </Box>
                </DialogContent>
            </Dialog>

            <SinalizarStatus open={openSinalizarStatus} onClose={() => setOpenSinalizarStatus(false)} onStatusChange={setStatusOnlineLocal} darkMode={darkMode} />


            <Backdrop open={inGeracao} sx={{ zIndex: 9999, color: '#fff', flexDirection: 'column' }}>
                <CircularProgress color="inherit" /><Typography sx={{ mt: 2 }}>Processando...</Typography>
            </Backdrop>
            </div>
        </ThemeProvider>
    );
}

